import { z } from 'zod';
import { describeVocabularyProblem, vocabularyProblems } from './queryVocabulary';
import {
  apiClient,
  pageSchema,
  queryKeys,
  toListQuery,
  useApiClient,
  useApiMutation,
  useApiQuery,
  type ApiClient,
  type ListParams,
  type Page,
  type ZagruProblem,
} from '@zagru/platform';
import type { DataGridState } from '@zagru/design-system';
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query';

export type { Page, ListParams };

export type QueryOpts<T> = Omit<UseQueryOptions<T, ZagruProblem>, 'queryKey' | 'queryFn'>;
export type MutationOpts<TData, TVars> = UseMutationOptions<TData, ZagruProblem, TVars>;

/** Converts DataGrid state (0-based page, sort array, filters) into API list params, merging static filters. */
export function gridStateToListParams(
  state: Pick<DataGridState, 'page' | 'size' | 'sort' | 'filters'>,
  extraFilters: Record<string, string | undefined | null> = {},
): ListParams {
  const filter: Record<string, string> = {};
  for (const [k, v] of Object.entries(state.filters)) if (v !== '' && v != null) filter[k] = v;
  for (const [k, v] of Object.entries(extraFilters)) if (v) filter[k] = v;
  return { page: state.page, size: state.size, sort: state.sort, filter };
}

export interface ListResource<T> {
  context: string;
  resource: string;
  path: string;
  schema: z.ZodType<T>;
  pageSchema: z.ZodType<Page<T>>;
  keys: {
    all: readonly unknown[];
    list: (params?: ListParams) => readonly unknown[];
    detail: (id: string) => readonly unknown[];
  };
  list: (client: ApiClient, params?: ListParams, signal?: AbortSignal) => Promise<Page<T>>;
  get: (client: ApiClient, id: string, signal?: AbortSignal) => Promise<T>;
  useList: (params?: ListParams, options?: QueryOpts<Page<T>>) => ReturnType<typeof useApiQuery<Page<T>>>;
  useDetail: (id: string | undefined, options?: QueryOpts<T>) => ReturnType<typeof useApiQuery<T>>;
}

/** Declares a REST list/detail resource following the `/api/v1/<context>/<resource>` convention. */
export function defineListResource<T>(context: string, resource: string, schema: z.ZodType<T>, path?: string): ListResource<T> {
  const p = path ?? `/api/v1/${context}/${resource}`;
  const pSchema = pageSchema(schema) as z.ZodType<Page<T>>;
  const keys = {
    all: queryKeys.custom(context, resource),
    list: (params?: ListParams) => queryKeys.list(context, resource, params),
    detail: (id: string) => queryKeys.detail(context, resource, id),
  };
  const list = (client: ApiClient, params: ListParams = {}, signal?: AbortSignal) => {
    /*
     * A sort or filter the server does not know is dropped in silence, so it is caught here instead.
     *
     * Only outside production: a mismatch is a programming error that a test or a dev session must fail
     * on, and shipping a throw to an operator over a filter they cannot see would be the worse bug.
     */
    if (import.meta.env?.MODE !== 'production') {
      const problems = vocabularyProblems(`${context}.${resource}`, params);
      if (problems.length > 0) throw new Error(problems.map(describeVocabularyProblem).join(' '));
    }
    return client.get<Page<T>>(p, { query: toListQuery(params), schema: pSchema, signal });
  };
  const get = (client: ApiClient, id: string, signal?: AbortSignal) =>
    client.get<T>(`${p}/${encodeURIComponent(id)}`, { schema, signal });
  return {
    context,
    resource,
    path: p,
    schema,
    pageSchema: pSchema,
    keys,
    list,
    get,
    useList: (params = {}, options) => useApiQuery<Page<T>>(keys.list(params), (client, signal) => list(client, params, signal), options),
    useDetail: (id, options) =>
      useApiQuery<T>(keys.detail(id ?? ''), (client, signal) => get(client, id ?? '', signal), {
        enabled: !!id,
        ...(options ?? {}),
      }),
  };
}

export type ListOnlyResource<T> = Omit<ListResource<T>, 'get' | 'useDetail'>;

/**
 * A collection that publishes no `GET /{id}`.
 *
 * `defineListResource` declares both the collection and its detail route — and the generated endpoint manifest
 * takes it at its word, so declaring a detail route the backend does not publish fails the contract test. Use this
 * for collections that are searched but never fetched one at a time (identity role assignments, for instance,
 * publish only `GET` on the collection and `DELETE` on the item).
 */
export function defineListOnlyResource<T>(context: string, resource: string, schema: z.ZodType<T>, path?: string): ListOnlyResource<T> {
  const full = defineListResource(context, resource, schema, path);
  const { get: _get, useDetail: _useDetail, ...listOnly } = full;
  return listOnly;
}

export type DetailOnlyResource<T> = Omit<ListResource<T>, 'list' | 'useList' | 'pageSchema'>;

/**
 * A record that is read one at a time while its collection is only searched: the backend publishes `GET /{id}` and
 * `POST /search`, not `GET` on the collection (transport capacity reservations, P-26). The manifest generator records
 * only the detail route, so no unpublished collection GET is claimed.
 */
export function defineDetailOnlyResource<T>(context: string, resource: string, schema: z.ZodType<T>, path?: string): DetailOnlyResource<T> {
  const full = defineListResource(context, resource, schema, path);
  const { list: _list, useList: _useList, pageSchema: _pageSchema, ...detailOnly } = full;
  return detailOnly;
}

/** Generic mutation hook against an absolute API path; body is JSON, idempotency key auto-generated. */
export function usePost<TData = unknown, TVars = unknown>(
  path: string | ((vars: TVars) => string),
  options?: MutationOpts<TData, TVars> & { schema?: z.ZodType<TData>; method?: 'post' | 'put' | 'patch' | 'delete' },
) {
  const { schema, method = 'post', ...rest } = options ?? {};
  return useApiMutation<TData, TVars>((client, vars) => {
    const p = typeof path === 'function' ? path(vars) : path;
    if (method === 'delete') return client.delete<TData>(p, { idempotencyKey: true, schema });
    return client[method]<TData>(p, vars, { idempotencyKey: true, schema });
  }, rest);
}

export { useApiClient, useApiQuery, useApiMutation, z };

export const isoDateTime = z.string();
export const money = z.object({ amount: z.number(), currency: z.string().length(3) });
export const quantity = z.object({ value: z.number(), uom: z.string() });
export const idSchema = z.string();

/**
 * An entity's `resolveLabel` (names instead of ids): reads one record through the session's default client and
 * picks the name people use for it. A record that cannot be read resolves to `null` and the id is shown.
 */
export function labelResolver<T>(resource: Pick<ListResource<T>, 'get'>, pick: (record: T) => string | null | undefined): (id: string, signal?: AbortSignal) => Promise<string | null> {
  return (id, signal) =>
    resource
      .get(apiClient, id, signal)
      .then((record) => pick(record) ?? null)
      .catch(() => null);
}
